import API from 'src/api'
import utils from 'src/utils'
import { http } from '../boot/axios'
import { Capacitor } from '@capacitor/core'

async function normalizeImage (image) {
  if (!Capacitor.isNativePlatform()) return image

  if (typeof image === 'string' && image.startsWith('data:image')) {
    return image
  }

  if (typeof image === 'string' && (image.startsWith('file://') || image.startsWith('content://'))) {
    const { loadAndCompress } =
      await import('src/services/offline-images')

    const base64 = await loadAndCompress(image, 1200)

    if (typeof base64 === 'string' && base64.startsWith('data:image')) {
      return base64
    }

    return null
  }

  return image
}

async function normalizeQuickCheckPayload (payload) {
  if (!Capacitor.isNativePlatform()) return payload
  if (!payload || !Array.isArray(payload.tasks)) return payload

  const tasks = []

  for (const task of payload.tasks) {
    const t = { ...task }

    if (Array.isArray(t.pictures)) {
      const pics = []
      for (const img of t.pictures) {
        const normalized = await normalizeImage(img)
        if (normalized) pics.push(normalized)
      }
      t.pictures = pics
    }

    tasks.push(t)
  }

  return {
    ...payload,
    tasks
  }
}


export default {
  GRAPHQL_URL: API.GRAPHQL_URL + 'quickChecks',
  GRAPHQL_URL_TEMPORARY: API.GRAPHQL_URL + 'temporary',
  // Regular
  find (id) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/${id}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  createQC (quickCheck) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const normalizedPayload = await normalizeQuickCheckPayload(quickCheck)

          http.post(`${this.GRAPHQL_URL}`, normalizedPayload)
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  addTasksToQC (payload) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const normalizedPayload = await normalizeQuickCheckPayload(payload)

          http.post(`${this.GRAPHQL_URL}/addToQC`, normalizedPayload)
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  fetchFiltered (payload) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/filter`, { params: payload })
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  deleteTask (id) {
    return new Promise((resolve, reject) => {
      http.delete(`${this.GRAPHQL_URL}/${id}`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  geoLocationSelect (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/getLocation`, payload)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  dowloadQuickCheckPDF (quickCheck) {
    const reportName = quickCheck.name
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/quickCheckPDF`, { id: quickCheck.id }, {
        responseType: 'blob'
      })
        .then(async response => {
          if (Capacitor.isNativePlatform()) {
            utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
          } else {
            const { saveAs } = await import('file-saver')
            saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  getQCTasks (id) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/tasks/${id}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },

  // Temporary
  getTemporaryQC () {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL_TEMPORARY}`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  saveTemporary (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL_TEMPORARY}`, payload)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  saveTemporaryImage ({ data, type, index }) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          let normalizedData = data

          if (type === 'QC') {
            normalizedData = await normalizeImage(data)
          }

          http.post(
            `${this.GRAPHQL_URL_TEMPORARY}/image/${index}`,
            { data: normalizedData, type }
          )
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  deleteTemporary () {
    const data = {
      type: 'QC'
    }
    return new Promise((resolve, reject) => {
      http.delete(`${this.GRAPHQL_URL_TEMPORARY}`, { data })
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  quickCheckEdit (payload) {
    return new Promise((resolve, reject) => {
      http.put(`${this.GRAPHQL_URL}/edit/${payload.id}`, payload)
        .then(response => { resolve(response.data) })
        .catch(error => { reject(error) })
    })
  }
}
